import React, { useState } from 'react';
import Modal from '../ui/Modal';
import AiButton from './AiButton';
import AgeGroupSelect from '../common/AgeGroupSelect';
import { aiAPI } from '../../services/api';
import { getErrorMessage } from '../../utils/errorMessage';
import { refreshAiStatus } from '../../hooks/useAiStatus';
import type { AiTrainingPlanResult } from '../../types/ai';

export interface AiPlanDefaults {
  ageGroupId?: number | null;
  durationMinutes?: number | null;
}

interface AiTrainingPlanModalProps {
  onClose: () => void;
  defaults?: AiPlanDefaults;
  /** A kész terv: a hívó dönti el, hogy hozzáfűzi vagy lecseréli vele a meglévőt. */
  onGenerated: (result: AiTrainingPlanResult, mode: 'replace' | 'append') => void;
  /** Van-e már tartalom a tervben (ilyenkor megkérdezzük, cserélje vagy fűzze hozzá). */
  hasContent: boolean;
}

const inputClass =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

const TOPIC_EXAMPLES = ['Labdakihozatal hátulról', 'Befejezések kapura', '1v1 cselezés', 'Átmenetek, gyors visszarendeződés'];

/** Edzésterv generálása AI-val: téma, időtartam, létszám → blokkok a gyakorlattár felhasználásával. */
const AiTrainingPlanModal: React.FC<AiTrainingPlanModalProps> = ({ onClose, defaults, onGenerated, hasContent }) => {
  const [ageGroupId, setAgeGroupId] = useState<number | null>(defaults?.ageGroupId ?? null);
  const [duration, setDuration] = useState(String(defaults?.durationMinutes && defaults.durationMinutes > 0 ? defaults.durationMinutes : 75));
  const [topic, setTopic] = useState('');
  const [playerCount, setPlayerCount] = useState('');
  const [notes, setNotes] = useState('');
  const [useLibrary, setUseLibrary] = useState(true);
  const [mode, setMode] = useState<'replace' | 'append'>('replace');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    if (!topic.trim()) {
      setError('Add meg az edzés témáját.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await aiAPI.trainingPlan({
        ageGroupId,
        durationMinutes: parseInt(duration, 10) || 75,
        topic: topic.trim(),
        playerCount: playerCount ? parseInt(playerCount, 10) : null,
        notes: notes.trim() || null,
        useDrillLibrary: useLibrary,
      });
      onGenerated(result, hasContent ? mode : 'replace');
      onClose();
    } catch (err) {
      setError(getErrorMessage(err, 'Az AI most nem tudott edzéstervet írni.'));
    } finally {
      setLoading(false);
      refreshAiStatus();
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Edzésterv AI-val">
      <div className="space-y-4 pb-4">
        <p className="text-sm text-gray-500">
          Add meg, miről szóljon az edzés, és az AI összerak egy tervet bemelegítéstől levezetésig. A blokkokat utána
          szabadon átírhatod.
        </p>

        <label className="block text-sm font-medium text-gray-700">
          Téma, fő cél *
          <input
            type="text"
            maxLength={300}
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="pl. Labdakihozatal hátulról"
            className={`mt-1 ${inputClass}`}
            autoFocus
          />
        </label>
        <div className="flex flex-wrap gap-1.5">
          {TOPIC_EXAMPLES.map((example) => (
            <button
              key={example}
              type="button"
              onClick={() => setTopic(example)}
              className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-200"
            >
              {example}
            </button>
          ))}
        </div>

        <div>
          <span className="block text-sm font-medium text-gray-700 mb-1">Korosztály</span>
          <AgeGroupSelect value={ageGroupId} onChange={(id) => setAgeGroupId(id)} placeholder="Nincs megadva" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm font-medium text-gray-700">
            Időtartam (perc)
            <input
              type="number"
              inputMode="numeric"
              min={20}
              max={180}
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className={`mt-1 ${inputClass}`}
            />
          </label>
          <label className="block text-sm font-medium text-gray-700">
            Létszám
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={60}
              value={playerCount}
              onChange={(e) => setPlayerCount(e.target.value)}
              placeholder="pl. 14"
              className={`mt-1 ${inputClass}`}
            />
          </label>
        </div>

        <label className="block text-sm font-medium text-gray-700">
          Egyéb kérés
          <textarea
            rows={2}
            maxLength={1000}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="pl. fél pálya, 2 kapus, sok labdaérintés, a végén kapura játék"
            className={`mt-1 ${inputClass}`}
          />
        </label>

        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={useLibrary}
            onChange={(e) => setUseLibrary(e.target.checked)}
            className="mt-0.5 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          />
          Válogasson a klub gyakorlattárából is (a rajzokkal együtt)
        </label>

        {hasContent && (
          <fieldset className="space-y-1 text-sm text-gray-700">
            <legend className="font-medium mb-1">A meglévő tervvel mi legyen?</legend>
            <label className="flex items-center gap-2">
              <input type="radio" checked={mode === 'replace'} onChange={() => setMode('replace')} className="h-4 w-4 text-primary-600" />
              Cserélje le
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" checked={mode === 'append'} onChange={() => setMode('append')} className="h-4 w-4 text-primary-600" />
              Fűzze hozzá a végére
            </label>
          </fieldset>
        )}

        {error && (
          <p className="rounded-md bg-red-50 p-3 text-sm text-red-700" role="alert">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Mégse
          </button>
          <AiButton variant="solid" onClick={run} loading={loading} loadingText="Tervezek… (fél perc is lehet)">
            Terv készítése
          </AiButton>
        </div>
      </div>
    </Modal>
  );
};

export default AiTrainingPlanModal;
